import React, { useState } from 'react';
import { styled } from 'styled-components';

import CompassHeader, { PageType } from 'Compass/components/CompassHeader';

import {
  generateProfileStart, getUserPhotoGroupsAndIdeas,
  regenerateStart
} from 'Common/api/DazzleInternalService';
import {
  GetUserPhotoGroupsAndIdeasResponse,
  IdeaData,
  PhotoGroupData
} from '@dazzle/api-client';

interface GeneratedIdea {
  title: string;
  text: string;
  confidence: number;
  sources?: string[];
  cover_photo_id?: string;
  photo_ids?: string[];
  button_text?: string;
  follow_up?: string;
  justification?: string;
  description?: string;
  url?: string;
}

type UserData = GetUserPhotoGroupsAndIdeasResponse;

const ALL_PROFILE_STAGES = [
  'fashion-style',
  'trips',
  'activities',
  'ideas-restaurants',
  'ideas-hotels',
  'ideas-learning',
  'ideas-gifts',
  'ideas-gadgets',
  'ideas-concerts',
  'ideas-playlists',
  'ideas-spa',
  'ideas-personal-care',
  'ideas-classes',
  'ideas-podcasts',
];

const IdeaLabPage = () => {
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateUserLid, setRegenerateUserLid] = useState('');
  const [regenerateMode, setRegenerateMode] = useState<'ideas' | 'photo_groups' | 'both'>('ideas');
  const [deleteExisting, setDeleteExisting] = useState(false);
  const [regenerateMessage, setRegenerateMessage] = useState('');
  const [generatedIdeasJson, setGeneratedIdeasJson] = useState<GeneratedIdea[] | null>(null);

  const [userDataLid, setUserDataLid] = useState('');
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loadingUserData, setLoadingUserData] = useState(false);
  const [userDataError, setUserDataError] = useState('');
  const [expandedIdeas, setExpandedIdeas] = useState<Set<number>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());

  const [profileUserLid, setProfileUserLid] = useState('');
  const [profileStageId, setProfileStageId] = useState('fashion-style');
  const [generatingProfileIdeas, setGeneratingProfileIdeas] = useState(false);
  const [profileIdeaMessage, setProfileIdeaMessage] = useState('');

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    regenerate: true,
    profileIdeas: true,
    userData: false
  });

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const onRegenerateIdeas = async () => {
    if (!regenerateUserLid) {
      setRegenerateMessage('Please enter a user LID');
      return;
    }

    const userLid = parseInt(regenerateUserLid, 10);
    if (isNaN(userLid)) {
      setRegenerateMessage('Invalid user LID');
      return;
    }

    setRegenerating(true);
    setRegenerateMessage('Regenerating...');
    setGeneratedIdeasJson(null);

    try {
      const result = await regenerateStart({
        userLid,
        mode: regenerateMode,
        deleteExisting
      });

      if (!result?.success) {
        setRegenerateMessage('Failed to start regeneration');
        setRegenerating(false);
        return;
      }

      setRegenerateMessage('Regeneration started successfully');
      setRegenerating(false);
    } catch (e) {
      setRegenerateMessage(`Error: ${e}`);
      setRegenerating(false);
    }
  };

  const onGenerateProfileIdeas = async () => {
    if (!profileUserLid) {
      setProfileIdeaMessage('Please enter a user LID');
      return;
    }

    const userLid = parseInt(profileUserLid, 10);
    if (isNaN(userLid)) {
      setProfileIdeaMessage('Invalid user LID');
      return;
    }

    setGeneratingProfileIdeas(true);
    setProfileIdeaMessage('Generating profile ideas...');

    try {
      const result = await generateProfileStart({
        userLid,
        stageId: profileStageId
      });

      if (result?.success) {
        setProfileIdeaMessage(`Generated ${result.ideas_count} ideas`);
      } else {
        setProfileIdeaMessage('Failed to generate profile ideas');
      }
    } catch (e) {
      setProfileIdeaMessage(`Error: ${e}`);
    } finally {
      setGeneratingProfileIdeas(false);
    }
  };

  const onGenerateAllProfileIdeas = async () => {
    if (!profileUserLid) {
      setProfileIdeaMessage('Please enter a user LID');
      return;
    }

    const userLid = parseInt(profileUserLid, 10);
    if (isNaN(userLid)) {
      setProfileIdeaMessage('Invalid user LID');
      return;
    }

    setGeneratingProfileIdeas(true);
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < ALL_PROFILE_STAGES.length; i++) {
      const stageId = ALL_PROFILE_STAGES[i];
      setProfileIdeaMessage(`Processing ${i + 1}/${ALL_PROFILE_STAGES.length}: ${stageId}...`);

      try {
        const result = await generateProfileStart({
          userLid,
          stageId
        });

        if (result?.success) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch {
        errorCount++;
      }
    }

    setProfileIdeaMessage(`Completed: ${successCount} succeeded, ${errorCount} failed`);
    setGeneratingProfileIdeas(false);
  };

  const fetchUserData = async () => {
    if (!userDataLid) {
      setUserDataError('Please enter a user LID');
      return;
    }

    const userLid = parseInt(userDataLid, 10);
    if (isNaN(userLid)) {
      setUserDataError('Invalid user LID');
      return;
    }

    setLoadingUserData(true);
    setUserDataError('');
    setUserData(null);

    try {
      const data = await getUserPhotoGroupsAndIdeas({
        userLid,
        includeS3Urls: true
      });

      if (data) {
        setUserData(data);
      } else {
        setUserDataError('No data found or error occurred');
      }
    } catch (e) {
      setUserDataError(`Error: ${e}`);
    } finally {
      setLoadingUserData(false);
    }
  };

  return <>
    <CompassHeader pageType={PageType.IdeaLab} />
    <Page>
      <Container>
        <PageTitle>Idea Lab</PageTitle>

        <Section>
          <SectionHeader onClick={() => toggleSection('regenerate')}>
            <SectionTitle>
              Regenerate IDEAS / Photo Groups
            </SectionTitle>
            <ExpandIcon $isExpanded={expandedSections['regenerate']}>▼</ExpandIcon>
          </SectionHeader>
          {expandedSections['regenerate'] && (
            <SectionContent>
              <FormGroup>
                <label>User LID:</label>
                <input
                  type="number"
                  value={regenerateUserLid}
                  onChange={(e) => setRegenerateUserLid(e.target.value)}
                  placeholder="Enter user LID"
                  disabled={regenerating}
                />
              </FormGroup>
              <FormGroup>
                <label>Mode:</label>
                <select
                  value={regenerateMode}
                  onChange={(e) => setRegenerateMode(e.target.value as 'ideas' | 'photo_groups' | 'both')}
                  disabled={regenerating}
                >
                  <option value="ideas">IDEAS Only</option>
                  <option value="photo_groups">Photo Groups Only</option>
                  <option value="both">Both</option>
                </select>
              </FormGroup>
              <FormGroup>
                <label>
                  <input
                    type="checkbox"
                    checked={deleteExisting}
                    onChange={(e) => setDeleteExisting(e.target.checked)}
                    disabled={regenerating}
                  />
                  Delete existing data before regenerating
                </label>
              </FormGroup>
              <button onClick={onRegenerateIdeas} disabled={regenerating}>
                {regenerating ? 'Regenerating...' : 'Regenerate'}
              </button>
              {regenerateMessage && (
                <MessageBox>{regenerateMessage}</MessageBox>
              )}
              {generatedIdeasJson && (
                <JsonContainer>
                  <h4>Generated Ideas JSON:</h4>
                  <pre>{JSON.stringify(generatedIdeasJson, null, 2)}</pre>
                </JsonContainer>
              )}
            </SectionContent>
          )}
        </Section>

        <Section>
          <SectionHeader onClick={() => toggleSection('profileIdeas')}>
            <SectionTitle>
              Generate Ideas from Profile
            </SectionTitle>
            <ExpandIcon $isExpanded={expandedSections['profileIdeas']}>▼</ExpandIcon>
          </SectionHeader>
          {expandedSections['profileIdeas'] && (
            <SectionContent>
              <FormGroup>
                <label>User LID:</label>
                <input
                  type="number"
                  value={profileUserLid}
                  onChange={(e) => setProfileUserLid(e.target.value)}
                  placeholder="Enter user LID"
                  disabled={generatingProfileIdeas}
                />
              </FormGroup>
              <FormGroup>
                <label>Profile Stage:</label>
                <select
                  value={profileStageId}
                  onChange={(e) => setProfileStageId(e.target.value)}
                  disabled={generatingProfileIdeas}
                >
                  <option value="fashion-style">Fashion & Style</option>
                  <option value="trips">Travel & Trips</option>
                  <option value="activities">Activities</option>
                  <option value="ideas-restaurants">Restaurants</option>
                  <option value="ideas-hotels">Hotels</option>
                  <option value="ideas-learning">Learning</option>
                  <option value="ideas-gifts">Gifts</option>
                  <option value="ideas-gadgets">Gadgets</option>
                  <option value="ideas-concerts">Concerts</option>
                  <option value="ideas-playlists">Playlists</option>
                  <option value="ideas-spa">Spa</option>
                  <option value="ideas-personal-care">Personal Care</option>
                  <option value="ideas-classes">Classes</option>
                  <option value="ideas-podcasts">Podcasts</option>
                </select>
              </FormGroup>
              <ButtonGroup>
                <button onClick={onGenerateProfileIdeas} disabled={generatingProfileIdeas}>
                  {generatingProfileIdeas ? 'Generating...' : 'Generate Profile Ideas'}
                </button>
                <button onClick={onGenerateAllProfileIdeas} disabled={generatingProfileIdeas}>
                  {generatingProfileIdeas ? 'Processing...' : 'Reprocess All Stages'}
                </button>
              </ButtonGroup>
              {profileIdeaMessage && (
                <MessageBox>{profileIdeaMessage}</MessageBox>
              )}
            </SectionContent>
          )}
        </Section>

        <Section>
          <SectionHeader onClick={() => toggleSection('userData')}>
            <SectionTitle>
              User Photo Groups & Ideas
            </SectionTitle>
            <ExpandIcon $isExpanded={expandedSections['userData']}>▼</ExpandIcon>
          </SectionHeader>
          {expandedSections['userData'] && (
            <SectionContent>
              <FormGroup>
                <label>User LID:</label>
                <input
                  type="text"
                  value={userDataLid}
                  onChange={(e) => setUserDataLid(e.currentTarget.value)}
                  placeholder="e.g. 123"
                />
              </FormGroup>
              <button onClick={fetchUserData} disabled={loadingUserData}>
                {loadingUserData ? 'Loading...' : 'Fetch Data'}
              </button>

              {userDataError && <MessageBox>{userDataError}</MessageBox>}

              {userData && (
                <>
                  <h4>Photo Groups ({userData.photo_groups?.length || 0})</h4>
                  <Grid>
                    {userData.photo_groups?.map((group: PhotoGroupData, i: number) => {
                      const photoIds = typeof group.photo_ids === 'string'
                        ? JSON.parse(group.photo_ids)
                        : group.photo_ids;
                      const photoCount = Array.isArray(photoIds) ? photoIds.length : 0;

                      const metadata = typeof group.group_metadata === 'string'
                        ? JSON.parse(group.group_metadata)
                        : group.group_metadata;

                      const isExpanded = expandedGroups.has(i);
                      const toggleExpanded = () => {
                        const newExpanded = new Set(expandedGroups);
                        if (isExpanded) {
                          newExpanded.delete(i);
                        } else {
                          newExpanded.add(i);
                        }
                        setExpandedGroups(newExpanded);
                      };

                      return (
                        <Card key={i} onClick={toggleExpanded} style={{ cursor: 'pointer' }}>
                          {!isExpanded && group.cover_photo_url && (
                            <img src={group.cover_photo_url} alt={group.group_title} />
                          )}
                          <div className="content">
                            <h5>{group.group_title} {isExpanded ? '▼' : '▶'}</h5>
                            <p>{metadata?.activity || 'No activity'}</p>
                            <small>{photoCount} photos</small>

                            {isExpanded && (
                              <div style={{ marginTop: '12px' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '8px' }}>
                                  {Array.isArray(photoIds) && photoIds.map((photoId: string, idx: number) => (
                                    <div key={idx} style={{
                                      border: '1px solid #ddd',
                                      borderRadius: '4px',
                                      padding: '4px',
                                      fontSize: '10px',
                                      wordBreak: 'break-all'
                                    }}>
                                      {photoId}
                                    </div>
                                  ))}
                                </div>
                                <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #eee' }}>
                                  <p><strong>Group Title:</strong> {group.group_title}</p>
                                  <p><strong>Suggested Tag:</strong> {group.suggested_tag || 'None'}</p>
                                  <p><strong>Confidence:</strong> {group.confidence_score ? (group.confidence_score * 100).toFixed(1) + '%' : 'N/A'}</p>
                                  {metadata && (
                                    <>
                                      <p><strong>Activity:</strong> {metadata.activity || 'N/A'}</p>
                                      <p><strong>Description:</strong> {metadata.description || 'N/A'}</p>
                                      <p><strong>Theme:</strong> {metadata.theme || 'N/A'}</p>
                                      <p><strong>Temporal Pattern:</strong> {metadata.temporal_pattern || 'N/A'}</p>
                                      <p><strong>Periodicity Insight:</strong> {metadata.periodicity_insight || 'N/A'}</p>
                                      <p><strong>Context Analysis:</strong> {metadata.context_analysis || 'N/A'}</p>
                                      <p><strong>Importance Reason:</strong> {metadata.importance_reason || 'N/A'}</p>
                                      <p><strong>Inferred Interests:</strong> {metadata.inferred_interests || 'N/A'}</p>
                                      {metadata.tavily_queries && (
                                        <p><strong>Tavily Queries:</strong> {Array.isArray(metadata.tavily_queries) ? metadata.tavily_queries.join(', ') : metadata.tavily_queries}</p>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </Card>
                      );
                    })}
                  </Grid>

                  <h4>Ideas ({userData.ideas?.length || 0})</h4>
                  <Grid>
                    {userData.ideas?.map((idea: IdeaData, i: number) => {
                      const content = typeof idea.suggestion_json === 'string'
                        ? JSON.parse(idea.suggestion_json)
                        : idea.suggestion_json;

                      const isExpanded = expandedIdeas.has(i);
                      const toggleExpanded = () => {
                        const newExpanded = new Set(expandedIdeas);
                        if (isExpanded) {
                          newExpanded.delete(i);
                        } else {
                          newExpanded.add(i);
                        }
                        setExpandedIdeas(newExpanded);
                      };

                      return (
                        <Card key={i} onClick={toggleExpanded} style={{ cursor: 'pointer' }}>
                          {idea.cover_photo_url && (
                            <img src={idea.cover_photo_url} alt={content.title} />
                          )}
                          <div className="content">
                            <h5>{content.title} {isExpanded ? '▼' : '▶'}</h5>
                            <p>{content.text}</p>
                            <div className="tags">
                              <Tag>{(content.confidence * 100).toFixed(0)}%</Tag>
                              <Tag>{content.button_text}</Tag>
                            </div>

                            {isExpanded && (
                              <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #eee' }}>
                                <p><strong>Title:</strong> {content.title}</p>
                                <p><strong>Text:</strong> {content.text}</p>
                                <p><strong>Description:</strong> {content.description || 'N/A'}</p>
                                <p><strong>Button Text:</strong> {content.button_text}</p>
                                <p><strong>Follow-up:</strong> {content.follow_up}</p>
                                <p><strong>Justification:</strong> {content.justification}</p>
                                <p><strong>Confidence:</strong> {(content.confidence * 100).toFixed(1)}%</p>
                                <p><strong>Sources:</strong> {content.sources?.join(', ') || 'None'}</p>
                                {content.url && <p><strong>URL:</strong> <a href={content.url} target="_blank" rel="noopener noreferrer">{content.url}</a></p>}
                                {content.cover_photo_id && <p><strong>Cover Photo ID:</strong> {content.cover_photo_id}</p>}
                                {content.photo_ids && <p><strong>Photo IDs:</strong> {Array.isArray(content.photo_ids) ? content.photo_ids.join(', ') : content.photo_ids}</p>}
                              </div>
                            )}
                          </div>
                        </Card>
                      );
                    })}
                  </Grid>

                  <JsonContainer>
                    <h4>Raw Data:</h4>
                    <pre>{JSON.stringify(userData, null, 2)}</pre>
                  </JsonContainer>
                </>
              )}
            </SectionContent>
          )}
        </Section>
      </Container>
    </Page>
  </>;
};

const Page = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Container = styled.div`
  display: flex;
  width: 100%;
  max-width: 1200px;
  flex-direction: column;
  gap: 24px;
  padding: 32px 24px;
`;

const PageTitle = styled.h1`
  margin: 0 0 8px 0;
  font-size: 28px;
  font-weight: 700;
  color: #1A1D2B;
`;

const Section = styled.div`
  width: 100%;
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid #E4E7EF;
  overflow: hidden;
  transition: box-shadow 0.15s ease;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  cursor: pointer;
  background: linear-gradient(135deg, #02033B 0%, #743D98 60%, #AC326C 100%);
  color: white;
  user-select: none;
  transition: background 0.2s ease;

  &:hover {
    background: linear-gradient(135deg, #060545 0%, #5D3080 60%, #8E2858 100%);
  }
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 18px;
  font-weight: 600;
`;

const ExpandIcon = styled.span<{ $isExpanded: boolean }>`
  font-size: 12px;
  transition: transform 0.3s ease;
  transform: ${(props) => (props.$isExpanded ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  background: #F8F9FC;

  button {
    align-self: flex-start;
    background: linear-gradient(135deg, #02033B 0%, #743D98 60%, #AC326C 100%);
    color: white;
    border: none;
    border-radius: 8px;
    padding: 10px 24px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 4px rgba(0, 138, 255, 0.3);

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 138, 255, 0.4);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
`;

const FormGroup = styled.div`
  margin-bottom: 12px;

  label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: #1A1D2B;
    margin-bottom: 4px;
  }

  input[type="number"],
  input[type="text"],
  select {
    width: 100%;
    max-width: 300px;
    padding: 8px 12px;
    border: 2px solid #E4E7EF;
    border-radius: 6px;
    font-size: 14px;
    transition: border-color 0.2s ease;

    &:focus {
      outline: none;
      border-color: #008AFF;
    }

    &:disabled {
      background: #F8F9FC;
      cursor: not-allowed;
    }
  }

  input[type="checkbox"] {
    margin-right: 8px;
  }

  label:has(input[type="checkbox"]) {
    display: flex;
    align-items: center;
    cursor: pointer;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const MessageBox = styled.div`
  margin-top: 12px;
  padding: 12px;
  border-radius: 6px;
  background: #F8F9FC;
  border: 1px solid #E4E7EF;
  color: #1A1D2B;
  font-size: 14px;
  white-space: pre-wrap;
`;

const JsonContainer = styled.div`
  margin-top: 12px;
  padding: 12px;
  border-radius: 6px;
  background: #ffffff;
  color: #1A1D2B;
  font-family: monospace;
  font-size: 12px;
  max-height: 500px;
  overflow-y: auto;
  border: 1px solid #E4E7EF;

  h4 {
    margin: 0 0 8px 0;
    font-size: 14px;
    color: #1A1D2B;
  }

  pre {
    margin: 0;
    font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
    font-size: 12px;
    color: #1A1D2B;
    white-space: pre-wrap;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const Card = styled.div`
  background: white;
  border: 1px solid #E4E7EF;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  img {
    width: 100%;
    height: 150px;
    object-fit: cover;
  }

  .content {
    padding: 12px;
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  h5 {
    margin: 0 0 8px 0;
    font-size: 16px;
    color: #1A1D2B;
  }

  p {
    margin: 0 0 12px 0;
    font-size: 14px;
    color: #5A6178;
    flex: 1;
  }

  small {
    font-size: 12px;
    color: #8890A5;
    margin-top: auto;
  }

  .tags {
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
    flex-wrap: wrap;
  }
`;

const Tag = styled.span`
  background: #F0F1F6;
  color: #5A6178;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`;

export default IdeaLabPage;
